import { useRef, useState } from 'react';
import type { z } from 'zod';

import type { Nullable } from '@repo/common';
import type { PictureInput } from '@repo/frontend-shared';
import {
  type FileResourceReference,
  PictureSavePersistenceError,
  type UploadedFileResource,
} from '@repo/ui/api/file-upload';
import type { CropMeta } from '@repo/ui/components/elements/Cropper/Cropper';
import type { optionalImageDataSchema } from '@repo/ui/constants/zod-schemas';
import { type FileUploadResult, useFile } from '@repo/ui/hooks/use-file';
import {
  combineUploadBatches,
  createUploadBatch,
  type UploadBatch,
  UploadCleanupError,
} from '@repo/ui/utils/upload-batch';

import { invariantWithLog } from '../utils/invariant-with-log';

type ImageData = z.infer<typeof optionalImageDataSchema>;

function hasPictureData(picture: NonNullable<ImageData>): boolean {
  return Boolean(picture.original || picture.thumbnail || picture.meta || picture.originalId || picture.thumbnailId);
}

type usePictureProps = {
  original?: Nullable<FileResourceReference>;
  thumbnail?: Nullable<FileResourceReference>;
};

interface UploadedPictureFiles {
  original: UploadedFileResource;
  thumbnail: UploadedFileResource;
}

interface PictureUploadCache {
  originalFile: File | null;
  originalSelection: number;
  thumbnailFile: File;
  batch: UploadBatch<UploadedPictureFiles>;
}

interface PictureUploadInvalidators {
  original: (resourceId: string) => void;
  thumbnail: (resourceId: string) => void;
}

function getReusablePictureUpload(
  cachedUpload: PictureUploadCache | null,
  originalFile: File | null,
  originalSelection: number,
  thumbnailFile: File,
): UploadBatch<UploadedPictureFiles> | null {
  if (
    cachedUpload?.originalFile === originalFile &&
    cachedUpload.originalSelection === originalSelection &&
    cachedUpload.thumbnailFile === thumbnailFile &&
    cachedUpload.batch.isReusable()
  ) {
    return cachedUpload.batch;
  }
  return null;
}

function collectFreshUploads(uploads: readonly FileUploadResult[]): UploadedFileResource[] {
  return uploads.flatMap(upload => (upload.fresh ? [upload.resource] : []));
}

function getCompletedUpload(result: PromiseSettledResult<FileUploadResult>): FileUploadResult | null {
  return result.status === 'fulfilled' ? result.value : null;
}

function invalidateFreshUpload(upload: FileUploadResult | null, invalidate: (resourceId: string) => void): void {
  if (upload?.fresh) invalidate(upload.resource.id);
}

async function retryIncompleteImageCleanup(cleanupBacklog: Set<UploadBatch<unknown>>): Promise<void> {
  const cleanupAttempts = await Promise.all(
    [...cleanupBacklog].map(batch =>
      batch.cleanup().then(
        () => ({ succeeded: true, batch }) as const,
        (error: unknown) => ({ succeeded: false, error }) as const,
      ),
    ),
  );
  const cleanupFailures = cleanupAttempts.flatMap(attempt => {
    if (!attempt.succeeded) return [attempt.error];
    cleanupBacklog.delete(attempt.batch);
    return [];
  });

  if (cleanupFailures.length > 0) {
    throw new UploadCleanupError('Failed to retry cleanup for incomplete image uploads', cleanupFailures);
  }
}

function createPictureUploadBatch(
  originalUpload: FileUploadResult,
  thumbnailUpload: FileUploadResult,
  invalidators: PictureUploadInvalidators,
): UploadBatch<UploadedPictureFiles> {
  return createUploadBatch(
    { original: originalUpload.resource, thumbnail: thumbnailUpload.resource },
    collectFreshUploads([originalUpload, thumbnailUpload]),
    {
      onCleanup: () => {
        invalidateFreshUpload(originalUpload, invalidators.original);
        invalidateFreshUpload(thumbnailUpload, invalidators.thumbnail);
      },
    },
  );
}

function createIncompleteImageCleanupBatch(
  originalResult: PromiseSettledResult<FileUploadResult>,
  thumbnailResult: PromiseSettledResult<FileUploadResult>,
  invalidators: PictureUploadInvalidators,
): UploadBatch<null> {
  const originalUpload = getCompletedUpload(originalResult);
  const thumbnailUpload = getCompletedUpload(thumbnailResult);
  const completedUploads = [originalUpload, thumbnailUpload].flatMap(upload => (upload ? [upload] : []));

  return createUploadBatch(null, collectFreshUploads(completedUploads), {
    onCleanup: () => {
      invalidateFreshUpload(originalUpload, invalidators.original);
      invalidateFreshUpload(thumbnailUpload, invalidators.thumbnail);
    },
  });
}

async function throwFailedImageUpload(
  cleanupBatch: UploadBatch<null>,
  uploadFailures: readonly unknown[],
  cleanupBacklog: Set<UploadBatch<unknown>>,
): Promise<never> {
  try {
    await cleanupBatch.cleanup();
  } catch (cleanupError) {
    cleanupBacklog.add(cleanupBatch);
    throw new UploadCleanupError('Failed to upload image files and clean up completed uploads', [
      ...uploadFailures,
      cleanupError,
    ]);
  }
  throw new Error('Failed to upload image files');
}

export type PictureSaveResult = { succeeded: true } | { succeeded: false; error: unknown };

async function savePictureBatch(
  batch: UploadBatch<Nullable<PictureInput>>,
  onSave: (payload: PictureInput) => void | Promise<void>,
): Promise<void> {
  if (!batch.value) throw new Error('Image data is incomplete');
  try {
    await onSave(batch.value);
    batch.markPersisted();
  } catch (saveError) {
    if (saveError instanceof PictureSavePersistenceError && saveError.canCleanupUploads) {
      batch.confirmNotPersisted();
      try {
        await batch.cleanup();
      } catch (cleanupError) {
        throw new UploadCleanupError('Failed to save picture and clean up uploaded image files', [
          saveError,
          cleanupError,
        ]);
      }
      throw saveError;
    }
    batch.markUncertain();
    if (saveError instanceof PictureSavePersistenceError) throw saveError;
    throw new PictureSavePersistenceError(
      'Picture save could not be confirmed. Uploaded files were retained. Refresh your profile before retrying.',
      false,
      saveError,
    );
  }
}

export async function savePictureWithThumbnail(
  prepareThumbnail: () => Promise<Nullable<string>>,
  persistPicture: () => Promise<void>,
): Promise<PictureSaveResult> {
  return Promise.resolve()
    .then(async () => {
      const thumbnailResult = await prepareThumbnail();
      if (!thumbnailResult) {
        return { succeeded: false, error: new Error('Failed to process image thumbnail') } as const;
      }
      await persistPicture();
      return { succeeded: true } as const;
    })
    .then(
      result => result,
      (error: unknown) => ({ succeeded: false, error }) as const,
    );
}

type usePictureReturn = {
  originalUrl: Nullable<string>;
  thumbnailUrl: Nullable<string>;
  setOriginal: (file: File) => Promise<Nullable<string>>;
  setThumbnail: (file: File) => Promise<Nullable<string>>;
  uploadOriginal: (file?: Nullable<File>) => Promise<UploadedFileResource>;
  uploadThumbnail: (file?: Nullable<File>) => Promise<UploadedFileResource>;
  uploadImageFiles: (thumbnailFile: File, originalFile?: Nullable<File>) => Promise<UploadedPictureFiles>;
  uploadImageFilesBatch: (
    thumbnailFile: File,
    originalFile?: Nullable<File>,
  ) => Promise<UploadBatch<UploadedPictureFiles>>;
  uploadPictureBatch: (picture: ImageData) => Promise<UploadBatch<Nullable<PictureInput>>>;
  savePictureBatch: (
    batch: UploadBatch<Nullable<PictureInput>>,
    onSave: (payload: PictureInput) => void | Promise<void>,
  ) => Promise<void>;
  isSubmitting: boolean;
  resetPicture: () => void;
  restorePicture: () => Promise<void>;
  onPictureSave: (
    avatar: UploadedFileResource,
    thumbnail: UploadedFileResource,
    meta: CropMeta,
    onSave: (payload: PictureInput) => void | Promise<void>,
  ) => Promise<void>;
  uploadPicture: (picture: ImageData) => Promise<Nullable<PictureInput>>;
};

// A hook that wraps the useFile hook to handle both original and thumbnail images
export function usePicture({ original, thumbnail }: usePictureProps = {}): usePictureReturn {
  const {
    localFileUrl: originalUrl,
    loading: originalLoading,
    setFileData: setOriginalFile,
    uploadFileData: uploadOriginal,
    uploadFileWithProvenance: uploadOriginalWithProvenance,
    invalidateUploadedResource: invalidateOriginalResource,
    resetFile: resetOriginal,
    restoreFile: restoreOriginal,
  } = useFile(original);
  const {
    localFileUrl: thumbnailUrl,
    loading: thumbnailLoading,
    setFileData: setThumbnail,
    uploadFileData: uploadThumbnail,
    uploadFileWithProvenance: uploadThumbnailWithProvenance,
    invalidateUploadedResource: invalidateThumbnailResource,
    resetFile: resetThumbnail,
    restoreFile: restoreThumbnail,
  } = useFile(thumbnail);
  const originalSelection = useRef(0);
  const uploadCache = useRef<PictureUploadCache | null>(null);
  const [cleanupBacklog] = useState(() => new Set<UploadBatch<unknown>>());
  const invalidators = {
    original: invalidateOriginalResource,
    thumbnail: invalidateThumbnailResource,
  };

  const setOriginal = async (file: File): Promise<Nullable<string>> => {
    originalSelection.current += 1;
    return await setOriginalFile(file);
  };

  const resetPicture = () => {
    resetOriginal();
    resetThumbnail();
  };

  const restorePicture = async () => {
    await Promise.all([restoreOriginal(), restoreThumbnail()]);
  };

  const onPictureSave = async (
    original: UploadedFileResource,
    thumbnail: UploadedFileResource,
    meta: CropMeta,
    onSave: (payload: PictureInput) => void | Promise<void>,
  ) => {
    const cachedBatch = uploadCache.current?.batch;
    const pictureBatch =
      cachedBatch?.value.original.id === original.id && cachedBatch.value.thumbnail.id === thumbnail.id
        ? cachedBatch
        : createUploadBatch({ original, thumbnail }, []);

    try {
      await onSave({
        originalId: original.id,
        thumbnailId: thumbnail.id,
        meta,
      });
      pictureBatch.markPersisted();
    } catch (saveError) {
      if (saveError instanceof PictureSavePersistenceError && saveError.canCleanupUploads) {
        pictureBatch.confirmNotPersisted();
        try {
          await pictureBatch.cleanup();
        } catch (cleanupError) {
          throw new UploadCleanupError('Failed to save picture and clean up uploaded image files', [
            saveError,
            cleanupError,
          ]);
        }
        throw saveError;
      }

      if (saveError instanceof PictureSavePersistenceError) {
        pictureBatch.markUncertain();
        throw saveError;
      }

      pictureBatch.markUncertain();
      throw new PictureSavePersistenceError(
        'Picture save could not be confirmed. Uploaded files were retained. Refresh your profile before retrying.',
        false,
        saveError,
      );
    }
  };

  const uploadImageFilesBatch = async (
    thumbnailFile: File,
    originalFile?: Nullable<File>,
  ): Promise<UploadBatch<UploadedPictureFiles>> => {
    if (cleanupBacklog.size > 0) await retryIncompleteImageCleanup(cleanupBacklog);

    const sourceOriginalFile = originalFile ?? null;
    const sourceOriginalSelection = originalSelection.current;
    const cachedUpload = uploadCache.current;
    const reusableBatch = getReusablePictureUpload(
      cachedUpload,
      sourceOriginalFile,
      sourceOriginalSelection,
      thumbnailFile,
    );
    if (reusableBatch) return reusableBatch;
    if (cachedUpload) await cachedUpload.batch.cleanup();

    const [originalResult, thumbnailResult] = await Promise.allSettled([
      uploadOriginalWithProvenance(originalFile),
      uploadThumbnailWithProvenance(thumbnailFile),
    ]);

    if (originalResult.status === 'fulfilled' && thumbnailResult.status === 'fulfilled') {
      const batch = createPictureUploadBatch(originalResult.value, thumbnailResult.value, invalidators);
      if (originalSelection.current === sourceOriginalSelection) {
        uploadCache.current = {
          originalFile: sourceOriginalFile,
          originalSelection: sourceOriginalSelection,
          thumbnailFile,
          batch,
        };
      }
      return batch;
    }

    const cleanupBatch = createIncompleteImageCleanupBatch(originalResult, thumbnailResult, invalidators);
    const uploadFailures = [originalResult, thumbnailResult].flatMap(result =>
      result.status === 'rejected' ? [result.reason] : [],
    );
    return await throwFailedImageUpload(cleanupBatch, uploadFailures, cleanupBacklog);
  };

  const uploadImageFiles = async (
    thumbnailFile: File,
    originalFile?: Nullable<File>,
  ): Promise<UploadedPictureFiles> => {
    const batch = await uploadImageFilesBatch(thumbnailFile, originalFile);
    return batch.value;
  };

  const uploadPictureBatch = async (picture: ImageData): Promise<UploadBatch<Nullable<PictureInput>>> => {
    if (!picture) return createUploadBatch(null, []);

    const { original, thumbnail, meta, originalId } = picture;

    invariantWithLog(!original && !!thumbnail && !originalId, 'Cannot upload thumbnail image without original');
    invariantWithLog(!thumbnail && !!original, 'Cannot upload original image without thumbnail');

    if (!hasPictureData(picture)) {
      return createUploadBatch(null, []);
    }

    if (!(original || thumbnail)) {
      return createUploadBatch(null, []);
    }

    if (!(thumbnail && meta)) {
      throw new Error('Image data is incomplete');
    }

    if (!original && originalId) {
      const thumbnailUpload = await uploadThumbnailWithProvenance(thumbnail);
      const thumbnailBatch = createUploadBatch(thumbnailUpload.resource, collectFreshUploads([thumbnailUpload]), {
        onCleanup: () => invalidateFreshUpload(thumbnailUpload, invalidateThumbnailResource),
      });
      return combineUploadBatches({ originalId, thumbnailId: thumbnailUpload.resource.id, meta }, [thumbnailBatch]);
    }

    if (!original) {
      throw new Error('Image data is incomplete');
    }

    const uploadedFilesBatch = await uploadImageFilesBatch(thumbnail, original);
    const uploadedFiles = uploadedFilesBatch.value;

    const pictureInput = {
      originalId: uploadedFiles.original.id,
      thumbnailId: uploadedFiles.thumbnail.id,
      meta,
    };
    return combineUploadBatches(pictureInput, [uploadedFilesBatch]);
  };

  const uploadPicture = async (picture: ImageData): Promise<Nullable<PictureInput>> => {
    const batch = await uploadPictureBatch(picture);
    return batch.value;
  };

  return {
    originalUrl,
    thumbnailUrl,

    setOriginal,
    setThumbnail,

    uploadOriginal,
    uploadThumbnail,
    uploadImageFiles,
    uploadImageFilesBatch,

    uploadPicture,
    uploadPictureBatch,
    savePictureBatch,

    isSubmitting: originalLoading || thumbnailLoading,
    resetPicture,
    restorePicture,
    onPictureSave,
  };
}
